import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, inject, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router'

import { Store } from '@ngrx/store'

import { FlxIconModule, FlxModalModule } from '@flowx/angular-ui-toolkit'
import { AppVersionsStore } from '@flowx/designer/modules/process-details/components/branching-overlay/application-versions.store'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import { ApplicationVersion } from '@flowx/designer/shared/entities/application.entity'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { UrlParametersStoreService } from '@flowx/designer/shared/services/url-parameters-store.service'
import { selectNamedOutletsData } from '@flowx/designer/shared/store/selectors/router.selectors'

import {
  ConflictsStore,
  MergeConflicts,
  MergeStatus,
  ResumeMergeOptions,
} from '../../merge-conflicts.dictionary'
import { MergeConflictsService } from '../../services/merge-conflicts.service'
import { MergeConflictsStore } from '../../stores/conflicts.store'

export const readableApplicationsVersionsMetadataKeys = {
  mergeOverVersion: 'into',
  mergeFromVersion: 'Merging',
}

@Component({
  selector: 'flx-merge-conflicts-modal',
  imports: [NgClass, FlxModalModule, RouterOutlet, FlxSpinnerModule, FlxIconModule],
  templateUrl: './merge-conflicts-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    '[class.w-full]': 'isLarge()',
    'class': 'min-w-0',
  },
})
export class FlxMergeConflictsModalComponent {
  #mergeConflictsStore = inject(MergeConflictsStore)
  #appVersionsStore = inject(AppVersionsStore)
  #mergeConflictsService = inject(MergeConflictsService)
  #toastMessageService = inject(ToastMessageService)
  #router = inject(Router)
  #route = inject(ActivatedRoute)
  #urlParametersStoreService = inject(UrlParametersStoreService)
  #store = inject(Store)

  manualMergeCompleted = output<ApplicationVersion>()
  discardManualMerge = output<ConflictsStore['mergeConflictId']>()

  namedOutletsData = this.#store.selectSignal(selectNamedOutletsData)

  localLoader = signal<boolean>(false)

  isLarge = computed(() => this.namedOutletsData()['mergeConflicts']?.['largeModal'] ?? false)
  headerTitle = computed(() => this.namedOutletsData()['mergeConflicts']?.['title'])
  headerSubtitle = computed(() => this.namedOutletsData()['mergeConflicts']?.['subtitle'])

  isLoading = computed(() => {
    const mergeConflictsLoader = this.#mergeConflictsStore.isLoading()
    const mergeInProgress = this.#appVersionsStore.mergeInProgress()
    const localLoader = this.localLoader()

    return mergeConflictsLoader || mergeInProgress || localLoader
  })

  spinnerText = computed(() => {
    const isMergeInProgress = this.#appVersionsStore.mergeInProgress()
    const isLocalLoading = this.localLoader()

    if (isLocalLoading) {
      return 'Loading'
    }

    return isMergeInProgress ? 'Checking if any conflicts...' : 'Loading conflicts...'
  })

  constructor() {
    this.#mergeConflictsService.manualMergeProcessStarted$
      .pipe(takeUntilDestroyed())
      .subscribe((options) => this.merge(options))
    this.#mergeConflictsService.discardManualMergeProcess$
      .pipe(takeUntilDestroyed())
      .subscribe((mergeConflictId) => {
        if (!mergeConflictId) {
          this.manualMergeCompleted.emit(null)
          return
        }
        this.discardManualMerge.emit(mergeConflictId)
      })
  }

  async merge(options: ResumeMergeOptions): Promise<void> {
    const { appId, appVersionId } = this.#urlParametersStoreService.urlParameters
    const mergeConflictId = this.#mergeConflictsStore.mergeConflictId()

    this.localLoader.set(true)

    try {
      // Resume the merge process
      const merge = await this.#mergeConflictsStore.retryMerge(
        appId,
        appVersionId,
        mergeConflictId,
        options
      )
      // Get app version history once again to update the UI
      await this.#appVersionsStore.getHistory(appId, appVersionId)

      this.localLoader.set(false)
      // Handle post-merge actions based on merge status
      this.handleMergeResult(merge)
    } catch (error) {
      this.localLoader.set(false)
      this.#toastMessageService.showErrorMessage('Failed to merge branch')
      throw error
    }
  }

  private handleMergeResult(merge: MergeConflicts): void {
    const statusMessages: Partial<Record<MergeStatus, () => void>> = {
      [MergeStatus.MergeFailed]: () => {
        this.#mergeConflictsStore.setErrorData(merge)
        this.#router.navigate([{ outlets: { mergeConflicts: [merge.mergeId, 'errors'] } }], {
          relativeTo: this.#route.firstChild,
          queryParamsHandling: 'preserve',
        })
      },
      [MergeStatus.MergeConflicts]: () => {
        this.#router.navigate([{ outlets: { mergeConflicts: [merge.mergeId] } }], {
          relativeTo: this.#route.firstChild,
          queryParamsHandling: 'preserve',
        })
      },
      [MergeStatus.Merged]: () => {
        this.manualMergeCompleted.emit(merge.mergedAppVersion)
        this.#toastMessageService.showSuccessMessage('Merged successfully')
      },
    }

    statusMessages[merge.mergeStatus]?.()
  }
}
